import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@azim-crm/icons";
import styles from "./RowMenu.module.css";
const PANEL_WIDTH = 200;
const VIEWPORT_MARGIN = 8;
function computePosition(trigger) {
    const rect = trigger.getBoundingClientRect();
    const maxLeft = window.innerWidth - PANEL_WIDTH - VIEWPORT_MARGIN;
    const left = Math.max(VIEWPORT_MARGIN, Math.min(rect.right - PANEL_WIDTH, maxLeft));
    return { top: rect.bottom + 6, left };
}
/**
 * Menu de ações de linha do Azim CRM (RowMenu).
 * O painel renderiza em portal no `body`, posicionado a partir do retângulo
 * do gatilho — não em `position: absolute` dentro do próprio nó — porque a
 * `Table` do pacote envolve a tabela em `overflow-x: auto`, que recorta
 * qualquer painel que ultrapasse a borda do wrapper (DD-011).
 */
export function RowMenu({ label, items, disabled = false }) {
    const [open, setOpen] = useState(false);
    const [position, setPosition] = useState(null);
    const triggerRef = useRef(null);
    const panelRef = useRef(null);
    useLayoutEffect(() => {
        if (!open) {
            setPosition(null);
            return;
        }
        const trigger = triggerRef.current;
        if (!trigger)
            return;
        setPosition(computePosition(trigger));
    }, [open]);
    // Roda quando `position` deixa de ser `null` (painel já montado no portal) —
    // um efeito preso só a `[open]` dispararia na primeira renderização, antes
    // do painel existir, e nunca seria re-executado (mesma dependência).
    useEffect(() => {
        if (!open || !position)
            return;
        const firstItem = panelRef.current?.querySelector("[role='menuitem']:not(:disabled)");
        firstItem?.focus();
    }, [open, position]);
    useEffect(() => {
        if (!open)
            return;
        const handlePointerDown = (e) => {
            const target = e.target;
            if (triggerRef.current?.contains(target))
                return;
            if (panelRef.current?.contains(target))
                return;
            setOpen(false);
        };
        // Escape fecha e devolve o foco ao gatilho; setas navegam entre os itens.
        // Fica num listener de `window` (em vez de `onKeyDown` no próprio painel)
        // porque um elemento com role="menu" só pode ter handler de teclado se for
        // ele mesmo focável (jsx-a11y/interactive-supports-focus) — o foco real
        // vive nos `menuitem`s, o container nunca recebe foco diretamente.
        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                setOpen(false);
                triggerRef.current?.focus();
                return;
            }
            if (e.key !== "ArrowDown" && e.key !== "ArrowUp")
                return;
            e.preventDefault();
            const buttons = Array.from(panelRef.current?.querySelectorAll("[role='menuitem']:not(:disabled)") ?? []);
            if (buttons.length === 0)
                return;
            const currentIndex = buttons.indexOf(document.activeElement);
            const delta = e.key === "ArrowDown" ? 1 : -1;
            const nextIndex = (currentIndex + delta + buttons.length) % buttons.length;
            buttons[nextIndex]?.focus();
        };
        window.addEventListener("mousedown", handlePointerDown);
        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("mousedown", handlePointerDown);
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [open]);
    const handleItemSelect = (item) => {
        if (item.disabled)
            return;
        setOpen(false);
        triggerRef.current?.focus();
        item.onClick();
    };
    const panel = open && position
        ? createPortal(_jsx("div", { ref: panelRef, role: "menu", "aria-label": label, className: styles.panel, style: { top: position.top, left: position.left, width: PANEL_WIDTH }, children: items.map((item, i) => (_jsx("button", { type: "button", role: "menuitem", disabled: item.disabled, className: [styles.item, item.danger ? styles.itemDanger : ""].filter(Boolean).join(" "), onClick: () => handleItemSelect(item), children: item.label }, i))) }), document.body)
        : null;
    return (_jsxs("div", { className: styles.root, children: [_jsx("button", { ref: triggerRef, type: "button", className: styles.trigger, "aria-haspopup": "menu", "aria-expanded": open, "aria-label": label, disabled: disabled, onClick: () => setOpen((o) => !o), children: _jsx(Icon, { name: "more", size: 16 }) }), panel] }));
}
//# sourceMappingURL=RowMenu.js.map